import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Sprite from './Sprite';
import TypeBadge from './TypeBadge';
import DexFilterPanel from './DexFilterPanel';
import FilterDrawer from './FilterDrawer';
import NameTypeahead from './NameTypeahead';
import { useDexControls } from './dexControls';
import { getDexIndex } from '../services/pokemonService';
import { loadLearnsets } from '../services/learnsets';
import {
  activeChips,
  applyDexFilters,
  emptyFilters,
  readFilters,
  sortDex,
  sortLabel,
  writeFilters,
} from '../utils/dexFilters';
import { detailPath } from '../utils/dex';
import { formatDexNumber } from '../utils/pokemon';

function PokedexHome() {
  const [params, setSearchParams] = useSearchParams();
  const filters = useMemo(() => readFilters(params), [params]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stale, setStale] = useState(false);
  const [selected, setSelected] = useState(0);
  const [columns, setColumns] = useState(2);
  const [learnsets, setLearnsets] = useState(null);
  const [learnState, setLearnState] = useState('idle');
  const gridRef = useRef(null);
  const selectedRef = useRef(null);

  const setFilters = (next) => {
    setSearchParams(writeFilters(params, next), { replace: true });
  };

  const load = (fresh = false) => {
    let active = true;
    setLoading(true);
    setError('');
    getDexIndex({ fresh })
      .then((data) => {
        if (!active) return;
        setEntries(data.pokemon || []);
        setStale(Boolean(data.stale));
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        setError(err.message || 'The dex index is unavailable right now.');
        setLoading(false);
      });
    return () => {
      active = false;
    };
  };

  useEffect(() => load(false), []);

  useEffect(() => {
    if (filters.mode !== 'advanced' || learnsets || learnState === 'loading' || learnState === 'error') return undefined;
    let active = true;
    setLearnState('loading');
    loadLearnsets()
      .then((data) => {
        if (!active) return;
        setLearnsets(data);
        setLearnState('ready');
      })
      .catch(() => {
        if (!active) return;
        setLearnState('error');
      });
    return () => {
      active = false;
    };
  }, [filters.mode, learnsets, learnState]);

  const nameUniverse = useMemo(
    () => applyDexFilters(entries, { ...filters, query: '' }, learnsets),
    [entries, filters, learnsets]
  );
  const filtered = useMemo(
    () => sortDex(applyDexFilters(entries, filters, learnsets), filters),
    [entries, filters, learnsets]
  );
  const statBadge = sortLabel(filters);

  useEffect(() => {
    setSelected(0);
  }, [params]);

  useEffect(() => {
    const node = gridRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return undefined;
    const measure = () => {
      const count = getComputedStyle(node).gridTemplateColumns.split(' ').filter(Boolean).length;
      setColumns(Math.max(1, count));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [filtered.length, loading]);

  useEffect(() => {
    selectedRef.current?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [selected]);

  const openSelected = () => {
    const entry = filtered[selected];
    if (!entry) return;
    selectedRef.current?.click();
  };

  useDexControls({
    onLeft: () => setSelected((index) => Math.max(0, index - 1)),
    onRight: () => setSelected((index) => Math.min(filtered.length - 1, Math.max(0, index + 1))),
    onUp: () => setSelected((index) => Math.max(0, index - columns)),
    onDown: () => setSelected((index) => Math.min(filtered.length - 1, index + columns)),
    onA: openSelected,
    onB: () => setFilters(emptyFilters(filters.mode)),
  });

  return (
    <div className="page">
      <div className="page-intro">
        <div>
          <h1>National Dex</h1>
          <p className="lede">
            Search by name, or open Filter for the full list. Arrows move. A opens the selected entry. B clears the filters.
          </p>
        </div>
      </div>

      <form
        className="dex-quick-search"
        role="search"
        data-filter-root="dex-search"
        onSubmit={(event) => event.preventDefault()}
      >
        <NameTypeahead
          id="dex-search"
          label="Search by name or number"
          entries={nameUniverse}
          query={filters.query}
          onQueryChange={(query) => setFilters({ ...filters, query })}
          onSelect={(entry) => {
            if (entry && !entry.unmatched) setFilters({ ...filters, query: entry.name });
          }}
          placeholder="Pikachu or 25"
        />
      </form>

      <FilterDrawer count={activeChips(filters).length}>
        <DexFilterPanel
          idPrefix="dex-filter"
          filters={filters}
          onChange={setFilters}
          entries={entries}
          nameUniverse={nameUniverse}
          resultCount={filtered.length}
          totalCount={entries.length}
          learnsets={learnsets}
          learnState={learnState}
          filterRoot={false}
        />
      </FilterDrawer>

      {loading && <p className="status" role="status">Loading the national dex…</p>}
      {error && (
        <div className="alert" role="alert">
          <p>{error}</p>
          <button className="btn btn-primary" type="button" onClick={() => load(true)}>Retry</button>
        </div>
      )}
      {stale && !error && (
        <p className="alert" role="status">Showing a saved copy. The latest index could not be reached.</p>
      )}

      {!loading && !error && filtered.length === 0 && (
        <p className="status">No Pokémon match these filters.</p>
      )}

      <div className="dex-grid" ref={gridRef}>
        {filtered.map((entry, index) => {
          const isSelected = index === selected;
          return (
            <Link
              key={entry.slug || entry.id}
              to={`/pokemon/${detailPath(entry)}`}
              className={isSelected ? 'dex-cell is-selected' : 'dex-cell'}
              aria-current={isSelected ? 'true' : undefined}
              tabIndex={isSelected ? 0 : -1}
              ref={isSelected ? selectedRef : undefined}
              onFocus={() => setSelected(index)}
            >
              <Sprite
                src={entry.sprite}
                fallback={entry.spriteFallback}
                alt=""
              />
              <span className="dex-no">{formatDexNumber(entry.id)}</span>
              {(entry.mega || entry.primal) && (
                <span className="forme-badge">{entry.primal ? 'Primal' : 'Mega'}</span>
              )}
              <span className="dex-name">{entry.name}</span>
              {statBadge && (
                <span className="dex-stat">{statBadge.label} {statBadge.value(entry)}</span>
              )}
              <span className="type-list">
                {(entry.types || []).map((pokemonType) => (
                  <TypeBadge key={pokemonType} type={pokemonType} />
                ))}
              </span>
              {isSelected && <span className="selected-flag">Selected</span>}
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export default PokedexHome;
